import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useConfigStore } from '@renderer/stores/configState'

interface NewspaperLayoutProps {
  children: React.ReactNode
  showMasthead?: boolean
  mastheadTitle?: string
  mastheadSubtitle?: string
  showDateline?: boolean
  disableOverflow?: boolean
  projectId?: string // Optional prop, will fall back to useParams if not provided
}

const NewspaperLayout: React.FC<NewspaperLayoutProps> = ({
  children,
  showMasthead = true,
  mastheadTitle,
  mastheadSubtitle,
  showDateline = true,
  disableOverflow = false,
  projectId: projectIdProp
}) => {
  const { config } = useConfigStore()
  
  // Get newspaper settings from config.customTheme, fallback to props, then defaults
  const newspaperTitle = config?.customTheme?.newspaperTitle || mastheadTitle || 'DREAMBOOTH NEWS'
  const newspaperDomain = config?.customTheme?.newspaperDomain || mastheadSubtitle || 'DREAMBOOTHSTUDIO.COM'
  const params = useParams()
  const projectId = projectIdProp || params.projectId || ''

  // State for volume and session count
  const [volume, setVolume] = useState<number>(1)
  const [sessionCount, setSessionCount] = useState<number>(0)

  const currentDate = new Date()
  const dateString = currentDate
    .toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
    .toUpperCase()

  // Fetch stats from API
  useEffect(() => {
    if (!projectId) {
      console.log('[NEWSPAPER LAYOUT] No projectId available, using defaults')
      return
    }

    const STATS_STORAGE_KEY = `dreambooth-newspaper-stats-${projectId}`

    // Check sessionStorage first
    const storedStats = sessionStorage.getItem(STATS_STORAGE_KEY)
    if (storedStats) {
      try {
        const parsed = JSON.parse(storedStats)
        setVolume(parsed.volume || 1)
        setSessionCount(parsed.sessionCount || 0)
        console.log('[NEWSPAPER LAYOUT] Loaded stats from sessionStorage:', parsed)
      } catch (error) {
        console.error('[NEWSPAPER LAYOUT] Failed to parse stored stats:', error)
      }
    }

    // Fetch from API
    const fetchStats = async (): Promise<void> => {
      try {
        const response = await fetch(
          `${process.env.VITE_API_URL}/api/newspaper/stats?projectId=${projectId}`,
          {
            credentials: 'include'
          }
        )

        if (response.ok) {
          const data = await response.json()
          const fetchedVolume = data.volume || 1
          const fetchedSessionCount = data.sessionCount || 0

          setVolume(fetchedVolume)
          setSessionCount(fetchedSessionCount)

          // Store in sessionStorage
          sessionStorage.setItem(
            STATS_STORAGE_KEY,
            JSON.stringify({
              volume: fetchedVolume,
              sessionCount: fetchedSessionCount
            })
          )

          console.log('[NEWSPAPER LAYOUT] Fetched stats from API:', data)
        } else {
          console.error('[NEWSPAPER LAYOUT] Failed to fetch stats:', response.status)
        }
      } catch (error) {
        console.error('[NEWSPAPER LAYOUT] Error fetching stats:', error)
      }
    }

    fetchStats()
  }, [projectId])

  // Increment session count function (can be called when session completes)
  const incrementSessionCount = React.useCallback((): void => {
    if (!projectId) return

    const STATS_STORAGE_KEY = `dreambooth-newspaper-stats-${projectId}`

    setSessionCount((prevCount) => {
      const newCount = prevCount + 1

      // Update sessionStorage
      const storedStats = sessionStorage.getItem(STATS_STORAGE_KEY)
      if (storedStats) {
        try {
          const parsed = JSON.parse(storedStats)
          parsed.sessionCount = newCount
          sessionStorage.setItem(STATS_STORAGE_KEY, JSON.stringify(parsed))
        } catch (error) {
          console.error('[NEWSPAPER LAYOUT] Failed to update stored stats:', error)
        }
      }

      console.log('[NEWSPAPER LAYOUT] Incremented session count to:', newCount)
      return newCount
    })
  }, [projectId])

  // Expose increment function globally for use in other components
  React.useEffect(() => {
    if (typeof window !== 'undefined' && projectId) {
      (
        window as unknown as { incrementNewspaperSessionCount?: () => void }
      ).incrementNewspaperSessionCount = incrementSessionCount
    }
  }, [incrementSessionCount, projectId])

  return (
    <div
      className={`w-full h-full bg-newspaper-white font-newspaper text-[#1a1a1a] flex flex-col ${disableOverflow ? 'overflow-hidden' : 'overflow-y-auto'}`}
    >
      {showMasthead && (
        <div className="border-t-[3px] border-b-[3px] border-double border-[#2a2a2a] py-4 text-center flex-shrink-0">
          <div className="font-newspaper font-bold text-6xl leading-tight uppercase tracking-wider text-[#1a1a1a]">{newspaperTitle}</div>
        </div>
      )}
      {showDateline && (
        <div className="flex-shrink-0 grid grid-cols-[1fr_auto_1fr_auto_1fr] items-center font-serif text-2xl uppercase tracking-widest text-[#1a1a1a] border-b border-[#2a2a2a] pb-2 mb-4 px-14">
          <span>
            Vol {volume}. no.{sessionCount}
          </span>
          <span className="px-8 text-5xl pt-2">*</span>
          <span className="text-center">{newspaperDomain}</span>
          <span className="px-8 text-5xl pt-2">*</span>
          <span className="text-right">{dateString}</span>
        </div>
      )}
      <div className="flex-1 min-h-0 flex flex-col px-14 py-10">
        {children}
      </div>
    </div>
  )
}

export default NewspaperLayout
